import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Button, MenuItem } from '@mui/material';
import DrawerShell from './DrawerShell';
import { FieldGroup } from '../PanelComerciales.styles';
import { panelTheme } from '../PanelComerciales.theme';
import {
    Commercial,
    COMMERCIAL_STATUS_OPTIONS,
    TEMPORAL_INACTIVATION_REASONS,
} from '../PanelComerciales.types';
import { isCurrency, isNonEmptyTeamName, isFutureOrTodayDate } from '../utils/validators';
import { formatCurrencyInput, formatCurrencyMX } from '../utils/currency';

export type CommercialEditMode = 'full' | 'team' | 'goal';

interface CommercialEditDrawerProps {
    open: boolean;
    mode: CommercialEditMode;
    commercial: Commercial | null;
    teamOptions: string[];
    onClose: () => void;
    onSave: (next: Commercial) => void;
}

const TITLES: Record<CommercialEditMode, string> = {
    full: 'Editar comercial',
    team: 'Cambiar equipo',
    goal: 'Ajustar meta Q2',
};

const DESCRIPTION = 'Completa la información y guarda los cambios.';

const todayIso = () => new Date().toISOString().slice(0, 10);

const CommercialEditDrawer: React.FC<CommercialEditDrawerProps> = ({
    open,
    mode,
    commercial,
    teamOptions,
    onClose,
    onSave,
}) => {
    const [draft, setDraft] = useState<Commercial | null>(commercial);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!commercial) {
            setDraft(null);
        } else if (commercial.status !== 'Activo') {
            // Si el commercial llega ya inactivo/bloqueado, pre-rellenamos los
            // campos auxiliares para que el formulario tenga defaults
            // razonables (motivo + desde + hasta cuando aplica).
            setDraft({
                ...commercial,
                statusSince: commercial.statusSince || todayIso(),
                statusReason:
                    commercial.statusReason
                    || (commercial.status === 'Bloqueado' ? 'inactivo' : 'vacaciones'),
            });
        } else {
            setDraft(commercial);
        }
        setError(null);
    }, [commercial, open]);

    const title = useMemo(() => TITLES[mode], [mode]);

    if (!draft) return null;

    const setField = <K extends keyof Commercial>(key: K, value: Commercial[K]) =>
        setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

    // Cuando el usuario cambia el status, inicializamos los campos auxiliares
    // (motivo / desde) si no estaban, para que el formulario quede coherente.
    const onStatusChange = (next: Commercial['status']) => {
        setDraft((prev) => {
            if (!prev) return prev;
            if (next === 'Activo') {
                return {
                    ...prev,
                    status: next,
                    statusReason: undefined,
                    statusSince: undefined,
                    tempBlockUntil: undefined,
                    absence: '-',
                };
            }
            // Inactivo (temporal) o Bloqueado (definitivo): aseguramos defaults.
            const baseReason = next === 'Bloqueado' ? 'inactivo' : (prev.statusReason || 'vacaciones');
            return {
                ...prev,
                status: next,
                statusReason: baseReason,
                statusSince: prev.statusSince || todayIso(),
                tempBlockUntil: next === 'Bloqueado' ? undefined : prev.tempBlockUntil,
            };
        });
    };

    // Devuelve el primer error de validación encontrado, o null si no hay
    // errores. Extraído del submit para mantener la complejidad cognitiva
    // por debajo del umbral; cada chequeo es un early-return claro.
    const validateDraft = (d: Commercial): string | null => {
        if ((mode === 'goal' || mode === 'full') && !isCurrency(d.goal)) {
            return 'Meta inválida';
        }
        if ((mode === 'team' || mode === 'full') && !isNonEmptyTeamName(d.team)) {
            return 'Equipo requerido';
        }
        if (mode !== 'full' || d.status === 'Activo') return null;
        if (!d.statusSince || !isFutureOrTodayDate(d.statusSince)) {
            return 'Fecha de inicio inválida';
        }
        if (d.status === 'Inactivo'
            && (!d.tempBlockUntil || !isFutureOrTodayDate(d.tempBlockUntil))) {
            return 'Fecha de fin inválida para desactivación temporal';
        }
        return null;
    };

    const submit = () => {
        if (!draft) return;
        const err = validateDraft(draft);
        if (err) {
            setError(err);
            return;
        }
        onSave(draft);
        onClose();
    };

    const showStatusExtras = mode === 'full' && draft.status !== 'Activo';

    return (
        <DrawerShell
            open={open}
            title={title}
            description={commercial ? `${commercial.name} · ${commercial.team} · ${DESCRIPTION}` : DESCRIPTION}
            onClose={onClose}
        >
            {(mode === 'full') && (
                <FieldGroup>
                    <label>Nombre</label>
                    <TextField
                        size='small'
                        value={draft.name}
                        onChange={(e) => setField('name', e.target.value)}
                    />
                </FieldGroup>
            )}
            {(mode === 'full' || mode === 'team') && (
                <FieldGroup>
                    <label>Equipo</label>
                    <TextField
                        size='small'
                        select
                        value={draft.team}
                        onChange={(e) => setField('team', e.target.value)}
                    >
                        {teamOptions.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </TextField>
                </FieldGroup>
            )}
            {(mode === 'full' || mode === 'goal') && (
                <FieldGroup>
                    <label>Meta Q2</label>
                    <TextField
                        size='small'
                        value={formatCurrencyMX(draft.goal)}
                        onChange={(e) => setField('goal', formatCurrencyInput(e.target.value))}
                        inputProps={{ inputMode: 'numeric' }}
                        placeholder='$250,000'
                    />
                </FieldGroup>
            )}
            {(mode === 'full') && (
                <FieldGroup>
                    <label>Estado</label>
                    <TextField
                        size='small'
                        select
                        value={draft.status}
                        onChange={(e) => onStatusChange(e.target.value as Commercial['status'])}
                        helperText={
                            draft.statusSource === 'pipedrive'
                                ? 'Estado vigente sincronizado desde Pipedrive; al guardar el panel persistirá el nuevo encima.'
                                : undefined
                        }
                    >
                        {COMMERCIAL_STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </TextField>
                </FieldGroup>
            )}
            {showStatusExtras && draft.status === 'Inactivo' && (
                <FieldGroup>
                    <label>Motivo</label>
                    <TextField
                        size='small'
                        select
                        value={draft.statusReason ?? 'vacaciones'}
                        onChange={(e) => setField('statusReason', e.target.value)}
                    >
                        {TEMPORAL_INACTIVATION_REASONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                                {r.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </FieldGroup>
            )}
            {showStatusExtras && (
                <FieldGroup>
                    <label>Desde</label>
                    <TextField
                        size='small'
                        type='date'
                        value={draft.statusSince ?? ''}
                        onChange={(e) => setField('statusSince', e.target.value)}
                    />
                </FieldGroup>
            )}
            {showStatusExtras && draft.status === 'Inactivo' && (
                <FieldGroup>
                    <label>Hasta</label>
                    <TextField
                        size='small'
                        type='date'
                        value={draft.tempBlockUntil ?? ''}
                        onChange={(e) => setField('tempBlockUntil', e.target.value)}
                    />
                </FieldGroup>
            )}
            {(mode === 'full') && (
                <FieldGroup>
                    <label>Notas internas</label>
                    <TextField
                        size='small'
                        multiline
                        minRows={2}
                        value={draft.notes ?? ''}
                        onChange={(e) => setField('notes', e.target.value)}
                        placeholder='Visible solo para Gerencia Comercial.'
                    />
                </FieldGroup>
            )}
            {error ? <span style={{ color: panelTheme.colors.danger, fontSize: 12 }}>{error}</span> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    onClick={submit}
                    variant='contained'
                    sx={{
                        textTransform: 'none',
                        background: panelTheme.colors.indigo,
                        '&:hover': { background: panelTheme.colors.violet },
                    }}
                >
                    Guardar
                </Button>
            </div>
        </DrawerShell>
    );
};

export default CommercialEditDrawer;
